import React, { useEffect, useState } from "react";
import {
    Table,
    Button,
    DatePicker,
    Spin,
    Row,
    Col,
    Select,
    Space
} from "antd";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";

import { downloadScoresExcel, fetchScoreFilters, fetchScoreStatistics } from "../../api/mediator";
import ScoresResponse from "../../types/ScoresResponse";
import { parseDate } from "../../util/date";
import { downloadExcelData } from "../../data/util";
import ScoreFiltersResponse from "../../types/ScoreFiltersResponse";
import Place from "../../types/Place";
import { parseNumberToString } from "../../util/common";
import { SearchStateType } from "./types";
import { initialFilterState, initialSearchState } from "./states";
import { useTexts } from "../../hooks/useTexts";

const MediatorScoreStatistics = () => {
    const { t } = useTexts();
    const [loading, setLoading] = useState(true);
    const [state, setState] = useState<ScoresResponse>({
        scores: [],
        pagination: {
            page: 0,
            pageSize: 10,
            pageCount: 0,
            elementCount: 0
        }
    });
    const [availableFilters, setAvailableFilters] = useState<ScoreFiltersResponse>(initialFilterState);
    const [searchState, setSearchState] = useState<SearchStateType>(initialSearchState);

    const getScores = (currentState: SearchStateType) => {
        let params: any = {
            placeId: currentState?.placeId || [],
            cityId: currentState?.cityId || [],
            dateFrom: getDateFrom(currentState),
            dateTo: getDateTo(currentState),
            page: currentState?.page ?? null,
            pageSize: currentState?.pageSize ?? null
        };
        setLoading(true);
        fetchScoreStatistics(params)
            .then(res => {
                if (!!res) {
                    setState(res);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const downloadExcel = async (currentState: SearchStateType) => {
        let params: any = {
            placeId: currentState?.placeId || [],
            cityId: currentState?.cityId || [],
            dateFrom: getDateFrom(currentState),
            dateTo: getDateTo(currentState),
            page: null,
            pageSize: null
        };
        const data = await downloadScoresExcel(params);
        downloadExcelData(data, "Ballar");
    };

    const getDateFrom = (currentState: SearchStateType) => {
        let dateFrom = parseDate(currentState.dateFrom);
        if (!dateFrom) dateFrom = `1970-01-01`;
        return dateFrom;
    };

    const getDateTo = (currentState: SearchStateType) => {
        const now = new Date();
        let dateTo = parseDate(currentState.dateTo);
        if (!dateTo) dateTo = `${now.getFullYear()}-12-31`;
        return dateTo;
    };

    const getScoreFilters = () => {
        fetchScoreFilters().then(response => {
            if (!!response) {
                setAvailableFilters(response);
            }
        }).catch(error => console.error("ERROR", error));
    };

    const clearFilter = () => {
        setSearchState(initialSearchState);
        getScores(initialSearchState);
    };

    useEffect(() => {
        getScores(searchState);
        getScoreFilters();
    }, []);

    const columns: ColumnsType<any> = [
        {
            title: '#',
            key: 'index',
            render: (text, record, index) => ((state.pagination.page - 1) * state.pagination.pageSize) + index + 1,
            width: 60,
        },
        {
            title: t('prescription', 'mediatorFirstname'),
            dataIndex: 'mediatorFullName',
            key: 'mediatorFullName',
        },
        {
            title: t('mediator', 'placeObject'),
            dataIndex: 'place',
            key: 'place',
        },
        {
            title: t('place', 'city'),
            dataIndex: 'city',
            key: 'city',
        },
        {
            title: t('mediator', 'giftScore'),
            dataIndex: 'gift',
            key: 'gift',
        },
        {
            title: t('mediator', 'initialScore'),
            key: 'initial',
            render: (_, record) => parseNumberToString(record.initial)
        },
        {
            title: t('mediator', 'score'),
            key: 'other',
            render: (_, record) => parseNumberToString(record.other)
        },
        {
            title: t('mediator', 'totalScore'),
            key: 'total',
            render: (_, record) => parseNumberToString(record.total)
        }
    ];

    return (
        <React.Fragment>
            <div style={{ marginTop: '20px' }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6}>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder={t('common', 'startDate')}
                            value={searchState.dateFrom ? dayjs(searchState.dateFrom) : null}
                            onChange={(date) => setSearchState({ ...searchState, dateFrom: date })}
                            format="DD/MM/YYYY"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder={t('common', 'endDate')}
                            value={searchState.dateTo ? dayjs(searchState.dateTo) : null}
                            onChange={(date) => setSearchState({ ...searchState, dateTo: date })}
                            format="DD/MM/YYYY"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={t('place', 'selectPlace')}
                            value={searchState.placeId}
                            onChange={(value) => setSearchState({ ...searchState, placeId: value })}
                            options={availableFilters.places?.map((item: Place) => ({ label: item.name, value: item.id }))}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={t('user', 'selectCity')}
                            value={searchState.cityId}
                            onChange={(value) => setSearchState({ ...searchState, cityId: value })}
                            options={availableFilters.cities?.map(city => ({ label: city.name, value: city.id }))}
                            allowClear
                        />
                    </Col>
                </Row>
            </div>

            <Row justify="space-between" align="middle" style={{ marginTop: '20px', marginBottom: '20px' }}>
                <Col>
                    <Button type="default" onClick={() => downloadExcel(searchState)}>
                        {t('prescription', 'downloadExcel')}
                    </Button>
                </Col>
                <Col>
                    <Space>
                        <Button type="primary" onClick={() => getScores(searchState)} loading={loading}>
                            {t('common', 'search')}
                        </Button>
                        <Button danger onClick={clearFilter}>
                            {t('prescription', 'clearFilter')}
                        </Button>
                    </Space>
                </Col>
            </Row>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : state.scores?.length > 0 ? (
                <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <Table
                        columns={columns}
                        dataSource={state.scores}
                        rowKey="mediatorId"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            current: (state.pagination?.page || 0) + 1,
                            pageSize: state.pagination?.pageSize || 10,
                            total: state.pagination?.elementCount || 0,
                            onChange: (page, pageSize) => getScores({ ...searchState, page: page - 1, pageSize })
                        }}
                    />
                </div>
            ) : (
                <h3 style={{ textAlign: 'center', marginTop: '50px' }}>{t('mediator', 'scoreNotFound')}</h3>
            )}
        </React.Fragment>
    );
};

export default MediatorScoreStatistics;